import { Table, TableHeaderRow } from '@devexpress/dx-react-grid';
import { useRef, useCallback } from 'react';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/typeof';

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");

  return _typeof(key) === "symbol" ? key : String(key);
}

function _toPrimitive(input, hint) {
  if (_typeof(input) !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];

  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (_typeof(res) !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }

  return (hint === "string" ? String : Number)(input);
}

var DEFAULT_FILTER_OPERATIONS = ['contains', 'notContains', 'startsWith', 'endsWith', 'equal', 'notEqual'];

var isTableHeadingCell = function isTableHeadingCell(tableColumn, tableRow) {
  return tableColumn.type === Table.COLUMN_TYPE && tableRow.type === TableHeaderRow.ROW_TYPE;
};

var isFilterValueEmpty = function isFilterValueEmpty(value) {
  return value === undefined || !String(value).length;
};

var isFilterOperationAnEmptyCheck = function isFilterOperationAnEmptyCheck(operation) {
  return operation === 'empty' || operation === 'notEmpty';
};

var isFilterValid = function isFilterValid(filter) {
  return filter !== null && (!isFilterValueEmpty(filter.value) || isFilterOperationAnEmptyCheck(filter.operation));
};

var getColumnFilterConfig = function getColumnFilterConfig(filters, columnName) {
  return (filters === null || filters === void 0 ? void 0 : filters.length) && filters.filter(function (filter) {
    return filter.columnName === columnName;
  })[0] || null;
};

var getColumnFilterOperations = function getColumnFilterOperations(getAvailableFilterOperations, columnName) {
  return getAvailableFilterOperations && getAvailableFilterOperations(columnName) || DEFAULT_FILTER_OPERATIONS;
};

var getSelectedFilterOperation = function getSelectedFilterOperation(columnFilter, columnFilterOperations) {
  if (columnFilter && columnFilter.operation) {
    return columnFilter.operation;
  }

  return columnFilterOperations[0];
};
/**
 * When each column filter renders it adds a function to the clearFilterStateFns object that can be used to clear that React
 * component's internal state. These functions are invoked when the clearFilters button is pressed and all filters are removed.
 */


var useManageClearFilterStateFns = function useManageClearFilterStateFns() {
  var clearFilterStateFns = useRef({});
  var addClearFilterStateFn = useCallback(function (columnName, filterStateFn) {
    clearFilterStateFns.current[columnName] = filterStateFn;
  }, []);
  var removeClearFilterStateFn = useCallback(function (columnName) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _clearFilterStateFns$ = clearFilterStateFns.current,
        filterStateFnToRemove = _clearFilterStateFns$[columnName],
        remainingFilterStateFns = _objectWithoutProperties(_clearFilterStateFns$, [columnName].map(_toPropertyKey));

    clearFilterStateFns.current = remainingFilterStateFns;
  }, []);
  return {
    clearFilterStateFns: clearFilterStateFns,
    addClearFilterStateFn: addClearFilterStateFn,
    removeClearFilterStateFn: removeClearFilterStateFn
  };
};
/**
 * This hook creates a function for the clearFilters button that clears both the grid's internal filter state and each column
 * filter component's internal state. It also wraps the grid's onFiltersChange prop so that if the clearFilters button is pressed
 * it only sends a notification to the parent component once at the end instead of once for each filter that is cleared.
 */


var useManageFilterState = function useManageFilterState(onFiltersChange) {
  var _useManageClearFilter = useManageClearFilterStateFns(),
      clearFilterStateFns = _useManageClearFilter.clearFilterStateFns,
      addClearFilterStateFn = _useManageClearFilter.addClearFilterStateFn,
      removeClearFilterStateFn = _useManageClearFilter.removeClearFilterStateFn;

  var wasClearFiltersPressed = useRef(false);
  var setWasClearFiltersPressed = useCallback(function (wasPressed) {
    wasClearFiltersPressed.current = wasPressed;
  }, [wasClearFiltersPressed]);
  var handleClearFilters = useCallback(function (filters, changeColumnFilter) {
    setWasClearFiltersPressed(true);
    filters.forEach(function (filter) {
      var columnName = filter.columnName;

      if (clearFilterStateFns.current[columnName]) {
        clearFilterStateFns.current[columnName]();
      }

      changeColumnFilter({
        columnName: columnName,
        config: null
      });
    });
  }, [wasClearFiltersPressed, setWasClearFiltersPressed]);
  var notifyFiltersChanged = useCallback(function (filters) {
    if (onFiltersChange && (!wasClearFiltersPressed.current || !(filters !== null && filters !== void 0 && filters.length))) {
      setWasClearFiltersPressed(false);
      onFiltersChange(filters);
    }
  }, [onFiltersChange, wasClearFiltersPressed, setWasClearFiltersPressed]);
  return {
    addClearFilterStateFn: addClearFilterStateFn,
    removeClearFilterStateFn: removeClearFilterStateFn,
    handleClearFilters: handleClearFilters,
    notifyFiltersChanged: notifyFiltersChanged
  };
};

var isEmptyObject = function isEmptyObject(value) {
  return value && value.constructor === Object && Object.keys(value).length === 0;
};

export { DEFAULT_FILTER_OPERATIONS, getColumnFilterConfig, getColumnFilterOperations, getSelectedFilterOperation, isEmptyObject, isFilterOperationAnEmptyCheck, isFilterValid, isFilterValueEmpty, isTableHeadingCell, useManageClearFilterStateFns, useManageFilterState };